import React from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Settings,
  Logout,
  DarkMode,
  LightMode,
  Brightness4,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { selectUser, logout } from '@/store/slices/authSlice';
import { selectUnreadNotifications, setTheme, selectTheme, selectThemeMode, setThemeMode } from '@/store/slices/uiSlice';
import { NotificationPanel } from '@/components/notifications/NotificationPanel';
import { GlobalSearch } from '@/components/search/GlobalSearch';
import { EnvironmentSwitcher } from '@/components/common/EnvironmentSwitcher';
import { useNavigate } from 'react-router-dom';

interface AppHeaderProps {
  onMenuClick: () => void;
}

export const AppHeader: React.FC<AppHeaderProps> = ({ onMenuClick }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const unreadNotifications = useAppSelector(selectUnreadNotifications);
  const theme = useAppSelector(selectTheme);
  const themeMode = useAppSelector(selectThemeMode);

  const [profileAnchor, setProfileAnchor] = React.useState<null | HTMLElement>(null);
  const [notificationsAnchor, setNotificationsAnchor] = React.useState<null | HTMLElement>(null);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchor(null);
  };

  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const handleThemeToggle = () => {
    if (themeMode === 'auto') {
      // If in auto mode, first cycle to manual light, then dark, then back to auto
      dispatch(setTheme('light'));
    } else if (theme === 'light') {
      dispatch(setTheme('dark'));
    } else {
      // Switch back to auto mode
      dispatch(setThemeMode('auto'));
    }
  };

  const getThemeIcon = () => {
    if (themeMode === 'auto') {
      return <Brightness4 />;
    }
    return theme === 'light' ? <DarkMode /> : <LightMode />;
  };

  const getThemeTooltip = () => {
    if (themeMode === 'auto') {
      return `Auto mode (currently ${theme})`;
    }
    return theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode';
  };

  const handleLogout = () => {
    // Clear Redux state and localStorage
    dispatch(logout());

    // Close profile menu
    handleProfileClose();

    // Navigate to admin login page
    navigate('/auth/login');
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
      }}
    >
      <Toolbar sx={{ minHeight: 64 }}>
        {/* Menu Button */}
        <IconButton
          edge="start"
          onClick={onMenuClick}
          sx={{ mr: 2, display: { md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        {/* Global Search */}
        <Box sx={{ maxWidth: 600, width: '100%', mr: 2, mt: 2 }}>
          <GlobalSearch />
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Environment Switcher */}
          <EnvironmentSwitcher />

          {/* Theme Toggle */}
          <Tooltip title={getThemeTooltip()}>
            <IconButton
              onClick={handleThemeToggle}
              sx={{
                color: 'text.secondary',
                position: 'relative',
                '&::after': themeMode === 'auto' ? {
                  content: '""',
                  position: 'absolute',
                  bottom: 2,
                  right: 2,
                  width: 6,
                  height: 6,
                  backgroundColor: 'primary.main',
                  borderRadius: '50%',
                } : {}
              }}
            >
              {getThemeIcon()}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <IconButton
            onClick={handleNotificationsClick}
            sx={{ color: 'text.secondary' }}
          >
            <Badge badgeContent={unreadNotifications.length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* Profile */}
          <IconButton onClick={handleProfileClick} sx={{ ml: 1 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: 'primary.main',
                fontSize: '0.875rem',
              }}
            >
              {user?.fullName?.charAt(0) || 'A'}
            </Avatar>
          </IconButton>
        </Box>
      </Toolbar>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={handleProfileClose}
        onClick={handleProfileClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {user?.fullName || 'Admin User'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email || 'admin@polarride.com'}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => navigate('/profile')}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Notifications Panel */}
      <NotificationPanel
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleNotificationsClose}
      />
    </AppBar>
  );
};